// ============================================================
// VISION EVENT BUS — Fase 9.15
// Realtime bus para eventos de Computer Vision
// Integra com riskEngine, operational_events, TV, War Room
// ============================================================
import type { VisionEvent, VisionSeverity, VisionEventTipo } from '@/types/vision'

type VisionHandler = (event: VisionEvent) => void

interface VisionBusSubscription {
  obraId: string
  handler: VisionHandler
}

// ─── In-memory pub/sub ────────────────────────────────────────
const subscribers: VisionBusSubscription[] = []
const eventHistory: Map<string, VisionEvent[]> = new Map()  // obraId → events[]
const MAX_HISTORY = 100

export function subscribeVisionBus(obraId: string, handler: VisionHandler): () => void {
  const sub: VisionBusSubscription = { obraId, handler }
  subscribers.push(sub)
  return () => {
    const idx = subscribers.indexOf(sub)
    if (idx !== -1) subscribers.splice(idx, 1)
  }
}

export function publishVisionEvent(event: VisionEvent): void {
  // Store in history
  const history = eventHistory.get(event.obra_id) ?? []
  history.unshift(event)
  if (history.length > MAX_HISTORY) history.pop()
  eventHistory.set(event.obra_id, history)

  // Notify subscribers
  for (const sub of subscribers) {
    if (sub.obraId === event.obra_id || sub.obraId === '*') {
      try { sub.handler(event) } catch { /* ignore handler errors */ }
    }
  }
}

export function getVisionHistory(obraId: string): VisionEvent[] {
  return eventHistory.get(obraId) ?? []
}

export function clearVisionHistory(obraId: string): void {
  eventHistory.delete(obraId)
}

// ─── Factory: create vision event ────────────────────────────
let _seq = 0

export function createVisionEvent(
  obraId:    string,
  tipo:      VisionEventTipo,
  severity:  VisionSeverity,
  title:     string,
  opts: Partial<Omit<VisionEvent, 'id' | 'obra_id' | 'tipo' | 'severity' | 'title' | 'ts'>> = {},
): VisionEvent {
  return {
    id:            `ve_${Date.now()}_${++_seq}`,
    obra_id:       obraId,
    tipo,
    severity,
    title,
    detail:        opts.detail,
    camera_id:     opts.camera_id,
    snapshot_id:   opts.snapshot_id,
    zone_id:       opts.zone_id,
    confidence:    opts.confidence ?? 0.75,
    bounding_boxes: opts.bounding_boxes ?? [],
    is_resolved:   false,
    metadata:      opts.metadata ?? {},
    ts:            new Date().toISOString(),
  }
}

// ─── Severity to operational severity bridge ─────────────────
export function visionSeverityToOperational(s: VisionSeverity): 'info' | 'warning' | 'critical' | 'emergency' {
  return s  // types are identical
}
